import React, { useState } from 'react';
import { MainContainer } from '../../index.styled';
import {
  Flex,
  Gap,
  HeaderWrapper,
  MainWrapper,
} from 'lib/components/styles/layout';
import { BreadCrumbs } from 'lib/components/breadcrumbs/BreadCrumbs';
import { useHistory, useLocation } from 'react-router';
import { Heading } from 'lib/components/styles/typography';
import { Button } from 'react-covideo-common';
import { HiPencil, HiTrash } from 'react-icons/hi';
import { BsLayersFill } from 'react-icons/bs';
import { DEFAULT_PAGE } from 'lib/const/PaginationConstants';
import {
  DEFAULT_DESIGNS_SIZE,
  LP_BUILDER_FORM_MODE,
  LP_BUILDER_MODAL_STEPS,
  LpBuilderFormMode,
} from '../constants';
import { useQueryGetSingleGroup } from 'lib/api/superadmin/landingPageDesignGroups/useQueryGetSingleGroup';
import { LandingPageDesignList } from '../landingPages/LandingPageDesignList';
import { SelectedLandingPageDesignsRibbon } from '../components/SelectedLandingPageDesignsRibbon';
import DeleteGroupModal from '../modals/DeleteGroupModal';
import { LoadingIndicator } from 'lib/components';
import CreateEditGroupModal from '../modals/CreateEditGroupModal';
import {
  ILandingPageBuilderForm,
  ILandingPageDesignGroup,
  ILandingPageDesignResponse,
  LP_BUILDER_MODAL_STEP,
} from '../types';
import { LandingPageBuilderModalForm } from '../components/modal/LandingPageBuilderModalForm';
import { useEditBatchTemplatesInGroup } from 'lib/api/superadmin/landingPageDesignGroups/useEditBatchTemplatesInGroup';
import { usePrepareLandingPageData } from '../helpers/usePrepareLandingPageData';
import { useEditLandingPageDesignMutation } from 'lib/api/superadmin/landingPageDesigns/useEditLandingPageDesignMutation';
import { ILandingPageBuilderBulkEditFormData } from '../helpers/getInitialLandingPageValuesForBulkEdit';
import { usePrepareLandingPageBulkData } from '../helpers/usePrepareLandingPageBulkData';
import { useAuth } from 'lib/context';
import { SuperAdminRole } from 'lib/const/SuperAdminRole';

export const SingleGroupPage = () => {
  const { prepare, isPreparing } = usePrepareLandingPageData();
  const { userData } = useAuth();
  const { prepareBulkData, isPreparingBulkData } =
    usePrepareLandingPageBulkData();
  const location = useLocation();
  const history = useHistory();
  const groupId = Number(location.pathname.split('/')[4]);
  const isResellerAdmin = userData.isResellerAdmin;
  const isCovideoSuperAdmin =
    userData.superAdminRole === SuperAdminRole.COVIDEO_SUPER_ADMIN;

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [limit, setLimit] = React.useState(DEFAULT_DESIGNS_SIZE);
  const [batchEditTemplates, setBatchEditTemplates] = useState<
    ILandingPageDesignResponse[]
  >([]);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // edit landing page modal
  const [step, setStep] = useState<LP_BUILDER_MODAL_STEP>(
    LP_BUILDER_MODAL_STEPS.design
  );
  // selected landing page design
  const [selectedLandingPageDesignId, setSelectedLandingPageDesignId] =
    useState<number | null>(null);
  // form mode
  const [modalState, setModalState] = useState<{
    mode: LpBuilderFormMode;
    open: boolean;
  }>({
    mode: LP_BUILDER_FORM_MODE.CREATE,
    open: false,
  });
  const [previewLandingPageId, setPreviewLandingPageId] = useState(0);

  const params = {
    start: page * (limit || DEFAULT_DESIGNS_SIZE),
    limit: limit || DEFAULT_DESIGNS_SIZE,
    groupId,
  };
  const { data, isLoading, isFetching } = useQueryGetSingleGroup(params);
  const isGroupLoading = isLoading || isFetching;
  const groupData = data || {
    title: '',
    landingPageDesignGroupId: 0,
    landingPageDesigns: { data: [], count: 0 },
  };
  const groupTitle = groupData.title;
  const group: ILandingPageDesignGroup = {
    title: groupTitle,
    landingPageDesignGroupId: groupId,
  };

  const onCloseLandingPageBuilderModalHandler = async () => {
    setStep(LP_BUILDER_MODAL_STEPS.design);
    setModalState(s => ({ ...s, open: false }));
  };

  // EDIT Design Mutation
  const {
    mutateAsync: editDesignMutation,
    isLoading: isLoadingEditDesignMutation,
  } = useEditLandingPageDesignMutation(onCloseLandingPageBuilderModalHandler);

  // BULK EDIT Design Mutation
  const {
    mutateAsync: bulkEditDesignMutation,
    isLoading: isLoadingBulkEditDesignMutation,
  } = useEditBatchTemplatesInGroup(onCloseLandingPageBuilderModalHandler);

  const isSubmitting =
    isLoadingEditDesignMutation ||
    isLoadingBulkEditDesignMutation ||
    isPreparing ||
    isPreparingBulkData;

  const onEditSubmit = async (values: ILandingPageBuilderForm) => {
    if (!selectedLandingPageDesignId) {
      return;
    }
    const payload = await prepare(values);
    await editDesignMutation({
      id: selectedLandingPageDesignId,
      ...payload,
    });
  };

  const onBulkEditSubmit = async (
    values: ILandingPageBuilderBulkEditFormData
  ) => {
    const data = await prepareBulkData(values);
    await bulkEditDesignMutation({ groupId, data });
  };

  const handleSubmit = async (
    values: ILandingPageBuilderForm | ILandingPageBuilderBulkEditFormData
  ) => {
    const isBulkEditMode = modalState.mode === LP_BUILDER_FORM_MODE.BULK_EDIT;
    isBulkEditMode
      ? await onBulkEditSubmit(values as ILandingPageBuilderBulkEditFormData)
      : await onEditSubmit(values as ILandingPageBuilderForm);
  };

  const landingPageDesigns = groupData.landingPageDesigns?.data || [];
  const landingPageDesignCount = groupData.landingPageDesigns?.count || 0;

  const breadcrumbLinks = [
    {
      url: '/admin/landing-pages-v2',
      title: 'Landing Pages',
    },
    {
      url: '/admin/landing-pages-v2/manage-groups',
      title: 'Manage Groups',
    },
    {
      url: `/admin/landing-pages-v2/manage-groups/${groupId}`,
      title: isGroupLoading ? '' : groupData.title,
    },
  ];

  const onPaginationChange = async ({
    page: newPage,
    size: newSize,
  }: {
    page: number;
    size: number;
  }) => {
    setPage(newPage);
    setLimit(newSize);
  };

  if (isGroupLoading) {
    return (
      <div>
        <LoadingIndicator isLoading={isGroupLoading} />
      </div>
    );
  }

  const handleOpenBulkEditModal = () => {
    setModalState({
      mode: LP_BUILDER_FORM_MODE.BULK_EDIT,
      open: true,
    });
  };

  return (
    <MainContainer>
      <MainWrapper resetPadding={false}>
        <BreadCrumbs links={breadcrumbLinks} />
        <Flex gap='40px'>
          <HeaderWrapper m='0px'>
            <Heading
              style={{
                maxWidth: '400px',
              }}
            >
              {groupTitle}
            </Heading>

            <Gap gap='8px'>
              <Button
                icon={<HiTrash size={20} />}
                text='Delete Group'
                variant='destructive'
                onClick={() => setIsDeleteModalOpen(true)}
              />
              <Button
                icon={<HiPencil size={20} />}
                text='Edit Group'
                variant='secondary'
                onClick={() => setIsEditModalOpen(true)}
              />
              <Button
                icon={<BsLayersFill />}
                text='Bulk Edit Landing Pages'
                disabled={!landingPageDesignCount}
                onClick={handleOpenBulkEditModal}
              />
            </Gap>
          </HeaderWrapper>

          <Flex gap='40px'>
            <Heading>Landing Page Templates</Heading>

            <LandingPageDesignList
              data={landingPageDesigns}
              count={landingPageDesignCount}
              isLoading={isGroupLoading}
              searchQuery={''}
              onPaginationChange={onPaginationChange}
              page={page}
              limit={limit}
              setSelectedTemplateDesignId={setSelectedLandingPageDesignId}
              setModalState={setModalState}
              setStep={setStep}
              batchEditTemplates={batchEditTemplates}
              setBatchEditTemplates={setBatchEditTemplates}
            />

            <SelectedLandingPageDesignsRibbon
              selectedTemplates={batchEditTemplates}
              setSelectedTemplates={setBatchEditTemplates}
              landingPageDesigns={landingPageDesigns}
              showRemoveFromGroup
              group={groupData}
            />
          </Flex>
        </Flex>

        {isDeleteModalOpen && (
          <DeleteGroupModal
            group={groupData}
            onClose={() => {
              history.push('/admin/landing-pages-v2/manage-groups');
              setIsDeleteModalOpen(false);
            }}
          />
        )}

        {isEditModalOpen && (
          <CreateEditGroupModal
            group={groupData}
            onClose={() => {
              setIsEditModalOpen(false);
            }}
          />
        )}

        {modalState.open && (
          <LandingPageBuilderModalForm
            onSubmit={values => handleSubmit(values)}
            isSubmitting={isSubmitting}
            mode={modalState.mode}
            group={group}
            selectedTemplateDesignId={selectedLandingPageDesignId}
            previewLandingPageId={previewLandingPageId}
            setPreviewLandingPageId={setPreviewLandingPageId}
            setStep={setStep}
            step={step}
            onClose={onCloseLandingPageBuilderModalHandler}
            isResellerAdmin={isResellerAdmin}
            isCovideoSuperAdmin={isCovideoSuperAdmin}
          />
        )}
      </MainWrapper>
    </MainContainer>
  );
};
